/**
 * SnapshotExtractor - Utility for extracting paragraph snapshots from layout results
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility handles extraction of paragraph snapshots from layout results,
 * which may come in different formats (nested snapshot property or direct snapshot).
 */

import type { ParagraphSnapshot } from '../../../core/types/Typesetter';

/**
 * Extract paragraph snapshots from layout results
 * 
 * Handles two possible formats:
 * 1. Layout objects with nested 'snapshot' property: { snapshot: ParagraphSnapshot, ... }
 * 2. Layout objects that ARE snapshots directly: ParagraphSnapshot
 * 
 * Filters out invalid snapshots (missing lines or empty).
 * 
 * @param layouts - Array of layout results (may be snapshots or objects with snapshot property)
 * @returns Array of valid paragraph snapshots with lines
 */
export function extractParagraphSnapshots(layouts: any[]): ParagraphSnapshot[] {
  return layouts
    .map((layout: any) => {
      // If layout has a nested 'snapshot' property, use that
      // Otherwise, layout IS the snapshot directly
      const snapshot = layout.snapshot || layout;
      return snapshot;
    })
    .filter((s: any) => s && s.lines && s.lines.length > 0);
}

/**
 * Validate that a snapshot has the required structure
 * 
 * @param snapshot - Snapshot to validate
 * @returns True if snapshot is valid (has lines array with content)
 */
export function isValidSnapshot(snapshot: any): snapshot is ParagraphSnapshot {
  return snapshot != null && 
         Array.isArray(snapshot.lines) && 
         snapshot.lines.length > 0;
}

